import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Flame, Zap, TrendingUp, Users, Briefcase, Search } from 'lucide-react';
import SeoHead from '@/components/shared/SeoHead';
import PageHero, { HeroStatTile } from '@/components/shared/PageHero';
import AdSlot from '@/components/shared/AdSlot';
import { LeaderboardAd, InContentAd } from '@/components/shared/AdSense';
import OccupationLink from '@/components/shared/OccupationLink';
import ShortageSignalBadge from '@/components/shared/ShortageSignalBadge';
import { useShortageSignals, type ShortageSignal } from '@/hooks/useShortageSignals';
import { getIndustryIcon } from '@/lib/industryIcons';

const InDemandCareers = () => {
  const [filterLevel, setFilterLevel] = useState<string>('all');
  const [sortBy, setSortBy] = useState<'score' | 'openings' | 'growth'>('score');

  const { data, isLoading } = useShortageSignals({
    minScore: 2,
    limit: 50,
  });

  const filteredSignals = useMemo(() => {
    if (!data?.signals) return [];
    let results = [...data.signals];

    if (filterLevel !== 'all') {
      results = results.filter(s => s.shortage_level === filterLevel);
    }

    switch (sortBy) {
      case 'openings':
        return results.sort((a, b) => b.annual_openings - a.annual_openings);
      case 'growth':
        return results.sort((a, b) => b.pct_change - a.pct_change);
      default:
        return results.sort((a, b) => b.shortage_score - a.shortage_score);
    }
  }, [data?.signals, filterLevel, sortBy]);

  const formatNumber = (num: number) =>
    new Intl.NumberFormat('en-US').format(Math.round(num));

  const toSlug = (title: string) =>
    title.toLowerCase().replace(/[^a-z0-9\s-]/g, '').replace(/\s+/g, '-');

  const criticalCount = data?.signals?.filter(s => s.shortage_level === 'critical-shortage').length || 0;
  const highDemandCount = data?.signals?.filter(s => s.shortage_level === 'high-demand').length || 0;
  const moderateCount = data?.signals?.filter(s => s.shortage_level === 'moderate-demand').length || 0;

  return (
    <>
      <SeoHead
        title="In-Demand Careers & Job Shortage Occupations 2025 | Wage Atlas"
        description="Discover careers with critical worker shortages and high hiring demand. Find occupations with the strongest job growth, largest annual openings, and best employment outlook from BLS data."
        canonical="/in-demand-careers/"
      />

      <div className="min-h-screen bg-background">
        {/* Hero Section */}
        <PageHero
          badge={{ icon: Briefcase, label: 'Labor Market Intelligence' }}
          title={
            <>
              In-Demand Careers &
              <span className="block mt-2">Job Shortage Occupations</span>
            </>
          }
          subtitle="Occupations experiencing critical worker shortages, rapid growth, and high annual openings. Based on BLS Employment Projections and O*NET Bright Outlook data."
        >
          <div className="grid grid-cols-3 gap-4 max-w-lg mx-auto">
            <HeroStatTile icon={Flame} value={criticalCount} label="Critical Shortage" />
            <HeroStatTile icon={Zap} value={highDemandCount} label="High Demand" />
            <HeroStatTile icon={TrendingUp} value={moderateCount} label="Growing Demand" />
          </div>
        </PageHero>

        <div className="container mx-auto px-4 py-8">
          <LeaderboardAd className="mb-8" />

          {/* Filters */}
          <div className="flex flex-wrap items-center gap-4 mb-8">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-muted-foreground">Filter:</span>
              <Select value={filterLevel} onValueChange={setFilterLevel}>
                <SelectTrigger className="w-48">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Demand Levels</SelectItem>
                  <SelectItem value="critical-shortage">Critical Shortage</SelectItem>
                  <SelectItem value="high-demand">High Demand</SelectItem>
                  <SelectItem value="moderate-demand">Moderate Demand</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-muted-foreground">Sort by:</span>
              <Select value={sortBy} onValueChange={(v: string) => setSortBy(v as 'score' | 'openings' | 'growth')}>
                <SelectTrigger className="w-48">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="score">Shortage Severity</SelectItem>
                  <SelectItem value="openings">Annual Openings</SelectItem>
                  <SelectItem value="growth">Growth Rate</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="ml-auto">
              <Badge variant="secondary" className="text-sm">
                {filteredSignals.length} occupations
              </Badge>
            </div>
          </div>

          {/* Results Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              {isLoading && (
                <div className="space-y-4">
                  {[...Array(6)].map((_, i) => (
                    <Card key={i}>
                      <CardContent>
                        <Skeleton className="h-6 w-3/4 mb-2" />
                        <Skeleton className="h-4 w-1/2 mb-4" />
                        <div className="flex gap-4">
                          <Skeleton className="h-8 w-24" />
                          <Skeleton className="h-8 w-24" />
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}

              {!isLoading && filteredSignals.length === 0 && (
                <Card>
                  <CardContent className="py-16 text-center">
                    <Search className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                    <h3 className="text-lg font-semibold mb-2">No Results</h3>
                    <p className="text-muted-foreground">
                      Try adjusting your filters to see more in-demand careers.
                    </p>
                  </CardContent>
                </Card>
              )}

              <div className="space-y-4">
                {filteredSignals.map((signal: ShortageSignal, index: number) => (
                  <Card key={signal.soc_code} className="hover:shadow-md transition-shadow">
                    <CardContent>
                      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <h3 className="text-lg font-semibold">
                              <OccupationLink
                                occupation={{
                                  occ_title: signal.soc_title,
                                  soc_code: signal.soc_code,
                                  slug: toSlug(signal.soc_title),
                                }}
                                className="hover:text-primary/80"
                              >
                                {signal.soc_title}
                              </OccupationLink>
                            </h3>
                          </div>

                          <div className="flex flex-wrap items-center gap-2 mb-3">
                            <ShortageSignalBadge signal={signal} size="sm" />
                            {signal.bright_outlook && (
                              <Badge variant="secondary" className="text-xs">
                                Bright Outlook
                              </Badge>
                            )}
                            <span className="text-xs text-muted-foreground">
                              SOC {signal.soc_code}
                            </span>
                          </div>

                          <div className="grid grid-cols-3 gap-4 text-sm">
                            <div>
                              <div className="text-muted-foreground text-xs">10-Year Growth</div>
                              <div className="font-semibold text-green-700 dark:text-green-400">
                                +{signal.pct_change}%
                              </div>
                            </div>
                            <div>
                              <div className="text-muted-foreground text-xs">Annual Openings</div>
                              <div className="font-semibold">{formatNumber(signal.annual_openings)}</div>
                            </div>
                            <div>
                              <div className="text-muted-foreground text-xs">Projected Employment</div>
                              <div className="font-semibold">{formatNumber(signal.proj_employment)}</div>
                            </div>
                          </div>
                        </div>

                        <Link
                          to={`/salary/${toSlug(signal.soc_title)}/united-states/`}
                          rel="nofollow"
                          className="shrink-0"
                        >
                          <Button size="sm">
                            View Salary
                          </Button>
                        </Link>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {filteredSignals.length > 10 && (
                <InContentAd />
              )}
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              <AdSlot slot="sidebar" />

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Why These Shortages?</CardTitle>
                </CardHeader>
                <CardContent className="text-sm text-muted-foreground space-y-3">
                  <p>
                    Labor shortages occur when the demand for workers in specific occupations
                    exceeds the available supply. Key drivers include:
                  </p>
                  <ul className="list-disc list-inside space-y-1">
                    <li>Aging workforce and retirements</li>
                    <li>Rapid industry growth outpacing training pipelines</li>
                    <li>Geographic mismatches between workers and jobs</li>
                    <li>Skills gaps from emerging technologies</li>
                  </ul>
                  <p>
                    Workers entering shortage occupations often benefit from higher wages,
                    signing bonuses, and stronger job security.
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Hardest-Hit Industries</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {[
                      { code: '62', name: 'Healthcare' },
                      { code: '23', name: 'Construction' },
                      { code: '48', name: 'Transportation' },
                      { code: '31', name: 'Manufacturing' },
                      { code: '72', name: 'Hospitality' },
                    ].map(ind => {
                      const IndustryIcon = getIndustryIcon(ind.code);
                      return (
                      <Link
                        key={ind.code}
                        to={`/industry/${ind.code}/united-states`}
                        className="flex items-center gap-2 p-2 rounded hover:bg-muted transition-colors"
                      >
                        <IndustryIcon className="h-4 w-4 text-muted-foreground shrink-0" aria-hidden />
                        <span className="text-sm font-medium">{ind.name}</span>
                        <Badge variant="outline" className="ml-auto text-xs bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-300">
                          Shortage
                        </Badge>
                      </Link>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>

              <Card className="border-primary/20 bg-gradient-to-br from-primary/5 to-transparent">
                <CardHeader>
                  <CardTitle className="text-lg">The AI Economy Effect</CardTitle>
                </CardHeader>
                <CardContent className="text-sm text-muted-foreground space-y-3">
                  <p>
                    Many of today's sharpest shortages trace back to the AI buildout: data centers,
                    grid expansion, and chip plants all compete for the same skilled workers.
                  </p>
                  <Link
                    to="/ai-economy-jobs"
                    className="text-primary hover:underline text-sm font-medium inline-block"
                  >
                    See AI economy jobs & salaries →
                  </Link>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Data Sources</CardTitle>
                </CardHeader>
                <CardContent className="text-sm text-muted-foreground">
                  <p className="mb-2">
                    Shortage signals are derived from:
                  </p>
                  <ul className="list-disc list-inside space-y-1">
                    <li>BLS Employment Projections (2023-2033)</li>
                    <li>O*NET Bright Outlook classifications</li>
                    <li>BLS JOLTS job openings rate</li>
                  </ul>
                  <Link
                    to="/data-sources"
                    className="text-primary hover:underline text-sm mt-3 inline-block"
                  >
                    View all data sources →
                  </Link>
                </CardContent>
              </Card>

              <AdSlot slot="in-content" />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default InDemandCareers;
